import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Globe, Check, ChevronDown } from "lucide-react";
import { t as tr } from "@/lib/tr";

export const LanguageSwitcher = ({ dark = false, loggedIn = false, scope = "landing_enabled", pathMode = false, showLabel = false }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [langs, setLangs] = useState([]);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    api.get("/public/i18n/languages")
      .then((r) => setLangs((r.data.languages || []).filter((l) => scope === "any" || l[scope])))
      .catch(() => setLangs([{ code: "de", native_name: "Deutsch" }, { code: "en", native_name: "English" }]));
  }, [scope]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = (i18n.language || "de").split("-")[0];
  const active = langs.find((l) => l.code === current || l.code === i18n.language);
  const shown = q ? langs.filter((l) => (l.native_name + l.code + (l.name_en || "")).toLowerCase().includes(q.toLowerCase())) : langs;

  const choose = async (code) => {
    setOpen(false);
    // Auswahl lokal UND (falls eingeloggt) am Konto persistieren – verhindert Zurückspringen
    localStorage.setItem("yg_lang", code);
    localStorage.setItem("yg_lang_user_choice", "1"); // Marker: explizite Nutzerwahl (hat Vorrang vor preferred_language)
    if (loggedIn || localStorage.getItem("rb_token")) {
      await api.put("/me/language", { preferred_language: code }).catch(() => {});
    }
    if (pathMode) {
      // Landing: sprach-spezifische URL (/en, /nl …) – Deutsch liegt auf "/"
      window.location.assign(code === "de" ? "/" : `/${code}`);
      return;
    }
    await i18n.changeLanguage(code);
    // ?lang=-Parameter entfernen, sonst überschreibt er nach dem Reload die Auswahl
    const url = new URL(window.location.href);
    url.searchParams.delete("lang");
    window.location.assign(url.toString());
  };

  if (langs.length < 2) return null;
  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button onClick={() => setOpen(!open)}
        aria-label={`${tr("app:s.spracheWaehlen")}: ${active?.native_name || current.toUpperCase()}`}
        className={`flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-full transition-colors ${dark ? "text-stone-300 hover:bg-stone-800" : "text-stone-700 hover:bg-stone-100"}`}
        data-testid="language-switcher-button">
        <Globe className="w-4 h-4" />
        <span className={showLabel ? "inline" : "hidden sm:inline"} data-testid="language-switcher-label">{active?.native_name || current.toUpperCase()}</span>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className={`absolute end-0 mt-2 w-56 rounded-xl border shadow-xl z-50 overflow-hidden ${dark ? "bg-stone-900 border-stone-700" : "bg-white border-stone-200"}`}
          data-testid="language-switcher-menu">
          {langs.length > 8 && (
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("app:s.suchen")}
              className={`w-full px-3 py-2 text-sm border-b outline-none ${dark ? "bg-stone-900 border-stone-700 text-stone-100" : "border-stone-100"}`}
              data-testid="language-search-input" />
          )}
          <div className="max-h-72 overflow-y-auto py-1">
            {shown.map((l) => (
              <button key={l.code} onClick={() => choose(l.code)}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm text-start ${dark ? "text-stone-200 hover:bg-stone-800" : "text-stone-700 hover:bg-stone-50"}`}
                data-testid={`language-option-${l.code}`}>
                <span>{l.native_name}</span>
                {(l.code === current || l.code === i18n.language) && <Check className="w-4 h-4 text-green-600" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
